import React from 'react';
import { Link, Routes, Route } from 'react-router-dom';
import { Shield, Lock, Globe, AlertTriangle } from 'lucide-react';
import { ApiEndpoint } from '../components/api/ApiEndpoint';
import { Environments } from './Environments';
import { Webhook } from './Webhook';
import { ChargeCard3DS } from './ChargeCard3DS';
import { ChargeCard2DS } from './ChargeCard2DS';
import { ChargeCardOTP } from './ChargeCardOTP';
import { AuthorizeCardTransaction } from './AuthorizeCardTransaction';
import { VerifyTransactionStatus } from './VerifyTransactionStatus';
import { Refund } from './Refund';
import { StatusCode } from './StatusCode';
import { TransactionHistory } from './TransactionHistory';

// TODO: Import serverToServerEndpoints from apiData once created
// import { serverToServerEndpoints } from '../data/apiData';

const ServerToServerHome: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Server-to-Server Documentation</h1>
        
        {/* Introduction */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
          <p className="text-white-muted mb-4 leading-relaxed">
            This document provides details on the Payment Gateway API, which allows merchants to process and manage transactions securely. The API supports:
          </p>
          <ul className="text-white-muted space-y-2 ml-6">
            <li className="flex items-start">
              <span className="text-lime mr-2">•</span>
              <span><strong>3D Secure (3DS) transactions</strong> for authentication and authorization.</span>
            </li>
            <li className="flex items-start">
              <span className="text-lime mr-2">•</span>
              <span><strong>Transaction authorization</strong> via OTP verification.</span>
            </li>
            <li className="flex items-start">
              <span className="text-lime mr-2">•</span>
              <span><strong>Transaction status verification</strong> to track payments.</span>
            </li>
            <li className="flex items-start">
              <span className="text-lime mr-2">•</span>
              <span><strong>Industry-standard security protocols</strong> for safe transaction processing.</span>
            </li>
          </ul>
          <p className="text-white-muted mt-4">
            All API requests must be made using <strong>HTTPS</strong> and include authentication headers.
          </p>
        </div>

        {/* Authentication & Security */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-6">Authentication & Security</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Shield className="h-5 w-5 text-red-500 mr-2" />
                <h3 className="text-lg font-semibold text-red-500">API Authentication</h3>
              </div>
              <ul className="text-xs text-red-500/80 space-y-2">
                <li>• All requests must be initiated from the server to protect the merchant's private key.</li>
                <li>• Requests must originate from whitelisted IP addresses associated with the private key.</li>
              </ul>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Lock className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-blue-500">Security Headers</h3>
              </div>
              <p className="text-xs text-blue-500/80">
                These headers must be included in all API requests for proper authentication and content type specification.
              </p>
            </div>
          </div>

          {/* Security Headers Table */}
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="px-6 py-4 bg-white/5 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">Security Headers</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-lime">Authorization</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{'{PRIVATE_KEY}'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Yes
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-white-muted">The merchant's private key used for authentication.</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-lime">Content-Type</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white-muted">application/json</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Yes
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-white-muted">Specifies that the request body is in JSON format.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-500 mb-2">Important Notes</h3>
                <ul className="text-sm text-yellow-500/90 space-y-2">
                  <li>• Create and set-up your business profile on <a href="https://sandbox-dashboard.nectargateway.com/auth/login" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 underline">https://sandbox-dashboard.nectargateway.com/auth/login</a></li>
                  <li>• Ensure the private key is securely stored and never exposed on the client side.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-lime text-dark rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Globe className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold">What's Next</h2>
          </div>
          <p className="text-dark/90 mb-4">
            Continue with environment configuration and API endpoint documentation
          </p>
          <Link
            to="/server-to-server/environments"
            className="inline-flex items-center px-6 py-2 bg-dark text-white font-medium rounded-lg hover:bg-dark/90 transition-colors"
          >
            Environments
          </Link>
        </div>
      </div>

      <div className="space-y-8">
        {/* TODO: Uncomment once serverToServerEndpoints is created in apiData */}
        {/* {serverToServerEndpoints.map((endpoint) => (
          <ApiEndpoint key={endpoint.id} endpoint={endpoint} />
        ))} */}
      </div>
    </div>
  );
};

export const ServerToServer: React.FC = () => {
  return (
    <Routes>
      <Route index element={<ServerToServerHome />} />
      <Route path="environments" element={<Environments />} />
      <Route path="webhook" element={<Webhook />} />
      <Route path="charge-card-3ds" element={<ChargeCard3DS />} />
      <Route path="charge-card-2ds" element={<ChargeCard2DS />} />
      <Route path="charge-card-otp" element={<ChargeCardOTP />} />
      <Route path="authorize-card-transaction" element={<AuthorizeCardTransaction />} />
      <Route path="verify-transaction-status" element={<VerifyTransactionStatus />} />
      <Route path="refund" element={<Refund />} />
      <Route path="status-code" element={<StatusCode />} />
      <Route path="transaction-history" element={<TransactionHistory />} />
      {/* Add more sub-routes here as needed */}
    </Routes>
  );
};